import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { 
  Info, 
  Mail, 
  Shield, 
  Heart,
  ExternalLink,
  MessageCircle,
  Star,
  Crown,
  Lock,
  Zap,
  Download,
  Smartphone,
  Monitor,
  CheckCircle,
  Share,
  Gift,
  HelpCircle
} from "lucide-react";

export default function AboutPage() {
  const { } = useAuth();
  const [showVersion, setShowVersion] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [APP_VERSION, setAppVersion] = useState("...");
  const [BUILD_DATE, setBuildDate] = useState("");

  // قراءة الإصدار ديناميكياً من version.json
  useEffect(() => {
    fetch('/version.json?t=' + Date.now(), { cache: 'no-store' })
      .then(r => r.json())
      .then(data => {
        if (data.version) setAppVersion(data.version);
        if (data.date) setBuildDate(data.date);
      })
      .catch(() => {});
  }, []);

  // التحقق من نوع الجهاز وحالة التثبيت
  useEffect(() => {
    // التحقق إذا iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(isIOSDevice);

    // التحقق إذا مثبت
    if (window.matchMedia("(display-mode: standalone)").matches) {
      setIsInstalled(true);
    }

    // الاستماع لحدث التثبيت
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  // تثبيت التطبيق
  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setIsInstalled(true);
    }
    setDeferredPrompt(null);
  };

  const features = [
    { icon: "📦", text: "إدارة المخزون والمنتجات" },
    { icon: "🛒", text: "نقطة البيع السريعة" },
    { icon: "💳", text: "إدارة ديون الزبائن والموردين" },
    { icon: "📊", text: "تقارير شاملة مع تصدير PDF و Excel" },
    { icon: "👥", text: "إدارة الموظفين والرواتب" },
    { icon: "💰", text: "صندوق النقدية مع الإيصالات" },
    { icon: "📅", text: "البيع بالتقسيط" },
    { icon: "⚡", text: "متابعة مباشرة للمبيعات" },
    { icon: "🔔", text: "تنبيهات المخزون المنخفض" },
    { icon: "📱", text: "دعم فني متكامل" },
  ];

  // الانتقال لصفحة الدعم
  const goToSupport = () => {
    window.location.href = '/support';
  };

  return (
    <div className="space-y-6 animate-fade-in-up pb-8" data-testid="about-page">
      {/* Header */}
      <div className="text-center">
        <div className="w-24 h-24 mx-auto rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" className="w-14 h-14">
            <path d="M4 4a2 2 0 012-2h12a2 2 0 012 2v16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm14 0H6v16h12V4zM8 6h8v4H8V6zm0 6h2v2H8v-2zm4 0h2v2h-2v-2zm4 0h2v2h-2v-2zm-8 4h2v2H8v-2zm4 0h2v2h-2v-2zm4 0h2v2h-2v-2z"/>
          </svg>
        </div>
        <h1 className="text-3xl font-bold text-white mb-2">
          Nazario-Hesabaty
        </h1>
        <p className="text-slate-400">
          نظام إدارة حسابات المتاجر
        </p>
        <button 
          onClick={() => setShowVersion(!showVersion)}
          className="mt-2 text-emerald-500 text-sm hover:underline"
        >
          الإصدار {APP_VERSION}
        </button>
        {showVersion && (
          <p className="text-slate-500 text-xs mt-1">
            تاريخ البناء: {BUILD_DATE}
          </p>
        )}
      </div>

      {/* 📱 تثبيت التطبيق */}
      <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/30">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-purple-400" />
            📲 تثبيت التطبيق على جهازك
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {isInstalled ? (
            <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-xl p-4 text-center">
              <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-2" />
              <p className="text-emerald-400 font-bold">✓ التطبيق مثبت بالفعل!</p>
              <p className="text-slate-400 text-sm mt-1">يمكنك فتحه من الشاشة الرئيسية</p>
            </div>
          ) : (
            <>
              {/* مميزات التطبيق */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-800/50 rounded-lg p-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  <span className="text-slate-300 text-sm">يعمل بدون إنترنت</span>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  <span className="text-slate-300 text-sm">سريع جداً</span>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  <span className="text-slate-300 text-sm">إشعارات فورية</span>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  <span className="text-slate-300 text-sm">ملء الشاشة</span>
                </div>
              </div>

              {/* زر التثبيت أو تعليمات iOS */}
              {deferredPrompt ? (
                <Button
                  onClick={handleInstall}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-6 text-lg"
                >
                  <Download className="w-6 h-6 me-2" />
                  تثبيت التطبيق الآن
                </Button>
              ) : isIOS ? (
                <div className="bg-slate-800/50 rounded-xl p-4 space-y-3">
                  <h4 className="text-white font-bold flex items-center gap-2">
                    <Smartphone className="w-5 h-5" />
                    خطوات التثبيت على iPhone/iPad:
                  </h4>
                  <ol className="text-slate-300 text-sm space-y-2 list-decimal list-inside">
                    <li>اضغط على زر المشاركة <Share className="w-4 h-4 inline-block mx-1" /> في Safari</li>
                    <li>اختر <strong>"إضافة إلى الشاشة الرئيسية"</strong></li>
                    <li>اضغط <strong>"إضافة"</strong></li>
                    <li>ستجد التطبيق على الشاشة الرئيسية! 🎉</li>
                  </ol>
                </div>
              ) : (
                <div className="bg-slate-800/50 rounded-xl p-4 space-y-3">
                  <h4 className="text-white font-bold flex items-center gap-2">
                    <Monitor className="w-5 h-5" />
                    خطوات التثبيت على Android/الكمبيوتر:
                  </h4>
                  <ol className="text-slate-300 text-sm space-y-2 list-decimal list-inside">
                    <li>اضغط على <strong>⋮</strong> (القائمة) في المتصفح</li>
                    <li>اختر <strong>"تثبيت التطبيق"</strong> أو <strong>"إضافة للشاشة الرئيسية"</strong></li>
                    <li>اضغط <strong>"تثبيت"</strong></li>
                    <li>ستجد التطبيق على الشاشة الرئيسية! 🎉</li>
                  </ol>
                </div>
              )}

              {/* الأجهزة المدعومة */}
              <div className="flex items-center justify-center gap-6 pt-2">
                <div className="flex items-center gap-2 text-slate-400 text-sm">
                  <Smartphone className="w-4 h-4" />
                  <span>Android</span>
                </div>
                <div className="flex items-center gap-2 text-slate-400 text-sm">
                  <Smartphone className="w-4 h-4" />
                  <span>iPhone</span>
                </div>
                <div className="flex items-center gap-2 text-slate-400 text-sm">
                  <Monitor className="w-4 h-4" />
                  <span>Windows</span>
                </div>
                <div className="flex items-center gap-2 text-slate-400 text-sm">
                  <Monitor className="w-4 h-4" />
                  <span>Mac</span>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Pricing */}
      <Card className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border-emerald-500/30">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Crown className="w-5 h-5 text-emerald-500" />
            السعر
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Free Trial */}
          <div className="bg-blue-500/10 rounded-xl p-4 border border-blue-500/30 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Gift className="w-6 h-6 text-blue-500" />
              <h3 className="text-white font-bold text-xl">تجربة مجانية</h3>
            </div>
            <p className="text-3xl font-bold text-blue-400 mb-2">5 أيام</p>
            <p className="text-slate-400 text-sm">جرّب جميع الميزات مجاناً!</p>
          </div>

          {/* السعر الموحد */}
          <div className="bg-emerald-500/10 rounded-xl p-6 border border-emerald-500/30 text-center">
            <h3 className="text-white font-bold text-xl mb-3">التطبيق الكامل</h3>
            <p className="text-4xl font-bold text-emerald-400 mb-2">690,000</p>
            <p className="text-slate-400 mb-4">دينار عراقي - دفعة واحدة</p>
            <div className="grid grid-cols-2 gap-2 text-sm text-slate-300">
              <div className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-500" /> نقطة البيع</div>
              <div className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-500" /> إدارة المخزون</div>
              <div className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-500" /> الزبائن والموردين</div>
              <div className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-500" /> التقارير المتقدمة</div>
              <div className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-500" /> إدارة الديون</div>
              <div className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-500" /> البيع بالتقسيط</div>
              <div className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-500" /> العقود الذكية</div>
              <div className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-500" /> النسخ الاحتياطي</div>
            </div>
          </div>

          {/* Payment Methods */}
          <div className="bg-slate-800/50 rounded-xl p-4">
            <h3 className="text-white font-bold mb-3 flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-emerald-400" />
              طرق الدفع:
            </h3>
            <div className="space-y-3">
              {/* سوبر كي */}
              <div className="bg-amber-900/30 rounded-lg p-3 border border-amber-500/30">
                <p className="text-amber-400 font-bold text-center mb-1">سوبر كي (Super Qi)</p>
                <p className="text-white text-center text-lg">7115634573</p>
              </div>
              {/* الرقم الجديد */}
              <div className="bg-emerald-900/30 rounded-lg p-4 border border-emerald-500/30">
                <p className="text-emerald-400 font-bold text-center mb-2">+9647772719901</p>
                <div className="flex flex-wrap justify-center gap-2">
                  <span className="bg-emerald-500/20 text-emerald-400 px-3 py-1 rounded-full text-sm">زين كاش</span>
                  <span className="bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full text-sm">اسيا باي</span>
                  <span className="bg-purple-500/20 text-purple-400 px-3 py-1 rounded-full text-sm">ناس واليت</span>
                </div>
              </div>
              {/* الدعم الفني */}
              <div className="text-center text-slate-400 text-sm">
                أو تواصل مع الدعم الفني للمساعدة
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Features */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-500" />
            مميزات التطبيق
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {features.map((feature, index) => (
              <div key={index} className="flex items-center gap-2 text-slate-300">
                <span>{feature.icon}</span>
                <span>{feature.text}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Contact */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-500" />
            للتواصل
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* رقم التواصل */}
          <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-xl p-4 border border-blue-500/30">
            <p className="text-center text-slate-300 mb-2">للتواصل والاستفسارات:</p>
            <p className="text-2xl font-bold text-center text-blue-400">+9647772719901</p>
          </div>

          <p className="text-slate-300 text-center">
            أو استخدم صفحة الدعم الفني لإرسال استفسارك
          </p>
          <div className="flex justify-center">
            <Button 
              onClick={goToSupport}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <HelpCircle className="w-4 h-4 me-2" />
              صفحة الدعم الفني
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Security */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-purple-500" />
            الأمان والخصوصية
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4 text-center">
            <div className="p-3">
              <Lock className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
              <p className="text-slate-300 text-sm">تشفير SSL/TLS</p>
            </div>
            <div className="p-3">
              <Shield className="w-8 h-8 text-blue-500 mx-auto mb-2" />
              <p className="text-slate-300 text-sm">بياناتك محمية</p>
            </div>
            <div className="p-3">
              <Zap className="w-8 h-8 text-amber-500 mx-auto mb-2" />
              <p className="text-slate-300 text-sm">تحديثات أمنية دورية</p>
            </div>
          </div>
          <p className="text-slate-400 text-sm text-center mt-4">
            نحن نحترم خصوصيتك. جميع بياناتك محفوظة بشكل آمن ولا يتم مشاركتها مع أي طرف ثالث.
          </p>
        </CardContent>
      </Card>

      {/* Credits */}
      <div className="text-center text-slate-500 text-sm">
        <p className="mt-1">تم بواسطة فريق نازاريو في قرية بارا تحتاني-العراق</p>
        <p className="mt-1">© 2026 جميع الحقوق محفوظة</p>
      </div>
    </div>
  );
}
